import {
  BrandLockup,
  FOCUS_RING,
  cn,
  linkClasses,
  useLanguage,
  usePageTitle,
  useTranslate,
} from '@athena/ui'
import type { ReactNode } from 'react'

import { marketingHome } from '../../lib/site.js'
import { AuthLanguageSwitch } from './AuthLanguageSwitch.js'

export interface AuthScreenProps {
  title: string
  subtitle: string
  /** The form, or whatever stands in for it once the visitor has a session. */
  children: ReactNode
  /**
   * The way back to the public site, under the form. On by default, for
   * sign-in and an invitation; off for onboarding, where the person already
   * has an account and leaving is not the next thing they need.
   */
  backToSite?: boolean
  /** The current moment in the entry → role → profile learning thread. */
  progressStage?: 1 | 2 | 3
  /** Marks the whole thread complete while the destination is prepared. */
  progressComplete?: boolean
}

/**
 * The screens a person sees on the way in: signing in, accepting an
 * invitation, and onboarding.
 *
 * ── Continuous with the public site, not a copy of it ────────────────────
 *
 * Someone pressing "Start for free" on `app-athena.com` should recognise the
 * page they land on. So it keeps what the public site is built from — the
 * off-white page background (it used to switch to white), the same lockup at
 * the same 32px in a header ruled off the same way, the same language switch
 * at the header's right — and none of what makes the public site editorial:
 * no display type, no film, no sections. The work of the page is one task.
 *
 * ── Two columns, no auth card ────────────────────────────────────────────
 *
 * The supporting column carries the page identity and one restrained Athena
 * learning thread. The working column remains the page canvas: no card,
 * border, shadow or nested surface around the form. Its `max-w-lg` cap is
 * 512px — close to the 520px auth measure — so fields stay comfortable
 * without leaving a small form floating in half of a wide desktop.
 *
 * ── The way back ─────────────────────────────────────────────────────────
 *
 * A quiet text link under the surface, to the public site in the page's own
 * language, and the header's lockup goes there too. Worded so it makes sense
 * to somebody who arrived from a bookmark rather than from the site.
 */
export function AuthScreen({
  title,
  subtitle,
  children,
  backToSite = true,
  progressStage = 1,
  progressComplete = false,
}: AuthScreenProps) {
  const t = useTranslate()
  const language = useLanguage()
  const home = marketingHome(language)
  // The heading is the page's name as well: "Sign in or create your account · Athena".
  usePageTitle(title)

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex min-h-header w-full max-w-container-max items-center justify-between gap-4 px-4 sm:px-6 md:px-10">
          <a href={home} className={cn('rounded-sm', FOCUS_RING)}>
            <BrandLockup className="h-8" />
            <span className="sr-only">{t('auth.siteHome')}</span>
          </a>
          <AuthLanguageSwitch />
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-container-max flex-1 gap-8 px-4 py-8 sm:px-6 sm:py-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,32rem)] lg:items-stretch lg:gap-16 lg:px-10">
        <section className="relative flex min-h-64 items-center overflow-hidden rounded-lg bg-primary-light px-6 py-8 sm:px-8 lg:min-h-[35rem] lg:px-12 lg:py-14">
          <div className="relative z-10 flex max-w-md flex-col justify-center gap-12">
            <BrandLockup className="hidden h-10 w-auto lg:block" />
            <div className="flex flex-col gap-3">
              <h1 tabIndex={-1} className="text-h1 font-bold text-text text-balance outline-none">
                {title}
              </h1>
              <p className="text-body text-text-secondary text-pretty">{subtitle}</p>
            </div>
          </div>

          <AuthProgress stage={progressStage} complete={progressComplete} />
        </section>

        <div className="flex w-full max-w-lg flex-col justify-center gap-6 lg:justify-self-end lg:py-12">
          {children}
          {backToSite ? (
            <a
              href={home}
              className={cn(linkClasses({ size: 'sm', layout: 'standalone' }), 'self-start')}
            >
              <span aria-hidden="true">← </span>
              {t('auth.backToSite')}
            </a>
          ) : null}
        </div>
      </main>
    </div>
  )
}

function AuthProgress({ stage, complete }: { stage: 1 | 2 | 3; complete: boolean }) {
  return (
    <div
      aria-hidden="true"
      data-stage={stage}
      data-complete={complete || undefined}
      className="auth-progress absolute bottom-0 right-10 top-0 hidden w-px lg:block"
    >
      <span className="absolute bottom-1/4 top-1/4 w-px bg-primary-border" />
      <span
        className={cn(
          'absolute top-1/4 w-px bg-accent transition-[height]',
          complete || stage === 3 ? 'h-1/2' : stage === 2 ? 'h-1/4' : 'h-0',
        )}
      />
      <ProgressNode position="top-1/4" state={nodeState(1, stage, complete)} />
      <ProgressNode position="top-1/2" state={nodeState(2, stage, complete)} />
      <ProgressNode position="bottom-1/4" state={nodeState(3, stage, complete)} />
    </div>
  )
}

function nodeState(index: 1 | 2 | 3, stage: 1 | 2 | 3, complete: boolean) {
  if (complete || index < stage) return 'complete'
  if (index === stage) return 'current'
  return 'pending'
}

function ProgressNode({
  position,
  state,
}: {
  position: 'top-1/4' | 'top-1/2' | 'bottom-1/4'
  state: 'complete' | 'current' | 'pending'
}) {
  return (
    <span
      className={cn(
        'absolute left-1/2 size-3 -translate-x-1/2 rounded-full border-2 transition-[background-color,border-color,transform]',
        position,
        state === 'complete' && 'border-accent-text bg-accent',
        state === 'current' && 'scale-125 border-primary bg-primary-light',
        state === 'pending' && 'border-primary-border bg-surface',
      )}
    />
  )
}
